import { createSlice } from '@reduxjs/toolkit';

/**
 * Staff Inventory state — four datasets, one per (integration × type).
 *
 * Each dataset owns its OWN `loading` and `error`. A single shared flag meant opening the
 * Gateways tab put the Locks tab back into a spinner, and the four tabs load in parallel
 * from the page, so they finish at four different times.
 *
 * The vendor is part of the key because the two read different pools: TTLock rows come
 * from our own tables, Control rows from the vendor's database. Same question, two sources.
 */
const dataset = () => ({ rows: [], loading: false, error: null, loaded: false });

const initialState = {
	control: { lock: dataset(), gateway: dataset() },
	ttlock: { lock: dataset(), gateway: dataset() }
};

/** Locate one dataset from an action payload; returns undefined for an unknown key. */
const target = (state, payload) => state[payload?.integration]?.[payload?.type];

const inventorySlice = createSlice({
	name: 'inventory',
	initialState,
	reducers: {
		// Payload: { integration: 'control'|'ttlock', type: 'lock'|'gateway' }
		loadInventory: (state, action) => {
			const it = target(state, action.payload);
			if (!it) return;
			it.loading = true;
			it.error = null;
		},
		// Payload: { integration, type, rows }
		loadInventorySuccess: (state, action) => {
			const it = target(state, action.payload);
			if (!it) return;
			it.rows = action.payload.rows || [];
			it.loading = false;
			it.loaded = true;
			it.error = null;
		},
		// Payload: { integration, type, error }. `rows` is left ALONE: a failed refresh
		// should keep showing the last good page rather than blanking the grid.
		loadInventoryError: (state, action) => {
			const it = target(state, action.payload);
			if (!it) return;
			it.loading = false;
			it.error = action.payload.error || 'Failed to load inventory.';
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const inventoryActions = inventorySlice.actions;

export default inventorySlice.reducer;
